import { Avatar, type AvatarSize, type AvatarStatus } from '@trustoo/components/Avatar/v2'
import clsx from 'clsx'
import { ComponentPropsWithoutRef, forwardRef } from 'react'

export type AvatarLabelGroupSize = 'sm' | 'md' | 'lg'

export interface AvatarLabelGroupProps extends Omit<
    ComponentPropsWithoutRef<'div'>,
    'children' | 'title'
> {
    size?: AvatarLabelGroupSize
    title: string
    supportingText?: string
    src?: string
    alt?: string
    initials?: string
    status?: AvatarStatus
    companySrc?: string
    companyAlt?: string
}

const AVATAR_SIZE_MAP: Record<AvatarLabelGroupSize, AvatarSize> = {
    sm: 'sm',
    md: 'md',
    lg: 'lg',
}

const TITLE_TEXT_CLASSES: Record<AvatarLabelGroupSize, string> = {
    sm: 'text-sm',
    md: 'text-sm',
    lg: 'text-md',
}

const SUPPORTING_TEXT_CLASSES: Record<AvatarLabelGroupSize, string> = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-md',
}

export const AvatarLabelGroup = forwardRef<HTMLDivElement, AvatarLabelGroupProps>(
    (
        {
            size = 'md',
            title,
            supportingText,
            src,
            alt,
            initials,
            status = 'none',
            companySrc,
            companyAlt,
            className,
            ...rest
        },
        ref,
    ) => (
        <div
            ref={ref}
            className={clsx('inline-flex min-w-0 items-center gap-spacing-md font-body', className)}
            {...rest}>
            <Avatar
                size={AVATAR_SIZE_MAP[size]}
                src={src}
                alt={alt}
                initials={initials}
                status={status}
                companySrc={companySrc}
                companyAlt={companyAlt}
                ring
            />

            <div className="flex min-w-0 flex-col items-start">
                <p
                    className={clsx(
                        'm-spacing-none truncate font-semibold text-text-primary',
                        TITLE_TEXT_CLASSES[size],
                    )}>
                    {title}
                </p>
                {supportingText && (
                    <p
                        className={clsx(
                            'm-spacing-none truncate font-regular text-text-tertiary',
                            SUPPORTING_TEXT_CLASSES[size],
                        )}>
                        {supportingText}
                    </p>
                )}
            </div>
        </div>
    ),
)

AvatarLabelGroup.displayName = 'AvatarLabelGroup'
